import { request } from 'graphql-request';
import { GRAPHQL_ENDPOINT } from '../constants';
import { 
  EQUIPMENT_BY_ID_QUERY, 
  EQUIPMENT_DETAIL_BY_ID_QUERY, 
  CATEGORIES_QUERY, 
  EQUIPMENTS_BY_CATEGORY_PAGINATED_QUERY, 
  EQUIPMENTS_BY_BRAND_NAME_PAGINATED_QUERY,
  SEARCH_EQUIPMENTS_QUERY,
  MULTI_FIELD_SEARCH_EQUIPMENTS_QUERY
} from '../graphql/queries';

/**
 * GraphQL 服务类
 */
class GraphQLService {
  constructor(endpoint = GRAPHQL_ENDPOINT) {
    this.endpoint = endpoint;
  }

  /**
   * 执行 GraphQL 查询
   * @param {string} query - GraphQL 查询
   * @param {Object} variables - 查询变量
   * @returns {Promise} 查询结果
   */
  async query(query, variables = {}) {
    try {
      return await request(this.endpoint, query, variables);
    } catch (error) {
      console.error('GraphQL query failed:', error);
      throw error;
    }
  }

  /**
   * 获取装备分类
   * @returns {Promise} 装备分类数据
   */
  async getEquipmentTypes() {
    const { EQUIPMENT_TYPES_QUERY } = await import('../graphql/queries');
    const data = await this.query(EQUIPMENT_TYPES_QUERY);
    return data.equipmentTypes || [];
  }

  /**
   * 获取推荐装备
   * @returns {Promise} 推荐装备数据
   */
  async getRecommendedEquipments() {
    const { RECOMMENDED_EQUIPMENTS_QUERY } = await import('../graphql/queries');
    const data = await this.query(RECOMMENDED_EQUIPMENTS_QUERY);
    return data.recommendedEquipments || [];
  }

  async getEquipmentById(id) {
    const data = await this.query(EQUIPMENT_BY_ID_QUERY, { id });
    return data.equipment;
  }

    /**
   * 获取种类
   * @returns {Promise} 三个种类
   */
    async getCategories() {
      const data = await this.query(CATEGORIES_QUERY);
      return data.categories || [];
    }

    // async getEquipmentsByCategory(category) {
    //   const data = await this.query(EQUIPMENT_BY_CATEGORY_QUERY, { category });
    //   return data.equipmentsByCategory || [];
    // }    

    /**
   * get equipment detail by id
   * @returns {Promise} 装备详情
   */
    async getEquipmentDetailById(id) {
      const data = await this.query(EQUIPMENT_DETAIL_BY_ID_QUERY, { id });
      return data.equipment || [];;
    }

    async getEquipmentsByCategoryPaginated(category, offset = 0, limit = 30) {
      const data = await this.query(EQUIPMENTS_BY_CATEGORY_PAGINATED_QUERY, { category, offset, limit });
      return data.equipmentsByCategory || [];
    }

    async getEquipmentsByBrandNamePaginated(brandName, offset = 0, limit = 30) {
      const data = await this.query(EQUIPMENTS_BY_BRAND_NAME_PAGINATED_QUERY, { brandName, offset, limit });
      return data.equipmentsByBrandName || [];
    }

    /**
     * 搜索装备（支持组合过滤）- 普通模式使用
     * @param {Object} filters - 搜索过滤条件
     * @param {string} filters.searchText - 搜索文本（仅搜索装备名称）
     * @param {string[]} filters.brands - 品牌列表
     * @param {string[]} filters.categories - 类别列表
     * @param {string[]} filters.styles - 风格列表
     * @param {number} filters.minPrice - 最低价格
     * @param {number} filters.maxPrice - 最高价格
     * @param {number} offset - 偏移量
     * @param {number} limit - 限制数量
     * @returns {Promise} 搜索结果
     */
    async searchEquipments(filters = {}, offset = 0, limit = 30) {
      const {
        searchText,
        brands,
        categories,
        styles,
        minPrice,
        maxPrice
      } = filters;

      const variables = {
        searchText: searchText || "",
        brands: brands?.length ? brands : null,
        categories: categories?.length ? categories : null,
        styles: styles?.length ? styles : null,
        minPrice: minPrice || null,
        maxPrice: maxPrice || null,
        offset: offset || 0,
        limit: limit || 30
      };

      // 添加调试日志
      console.log('🔍 普通搜索参数:', {
        searchText: variables.searchText,
        brands: variables.brands,
        categories: variables.categories,
        styles: variables.styles,
        minPrice: variables.minPrice,
        maxPrice: variables.maxPrice,
        offset: variables.offset,
        limit: variables.limit
      });

      const data = await this.query(SEARCH_EQUIPMENTS_QUERY, variables);
      
      // 添加结果日志
      console.log('🔍 普通搜索结果:', data.searchEquipments?.equipments?.length || 0, '条数据');
      console.log('🔍 分页信息:', {
        offset: data.searchEquipments?.offset,
        limit: data.searchEquipments?.limit,
        totalCount: data.searchEquipments?.totalCount
      });
      
      return data.searchEquipments?.equipments || [];
    }

    /**
     * 多字段搜索装备（搜索模式专用）
     * 搜索字段包括：name, brandName, color, model, gender, style, flex 等
     * @param {string} searchText - 搜索文本
     * @param {number} offset - 偏移量
     * @param {number} limit - 限制数量
     * @returns {Promise} 搜索结果
     */
    async multiFieldSearchEquipments(searchText, offset = 0, limit = 30) {
      const variables = {
        searchText: searchText || "",
        offset: offset || 0,
        limit: limit || 30
      };

      // 添加调试日志
      console.log('🔍 多字段搜索参数:', {
        searchText: variables.searchText,
        offset: variables.offset,
        limit: variables.limit
      });

      const data = await this.query(MULTI_FIELD_SEARCH_EQUIPMENTS_QUERY, variables);
      
      // 添加结果日志
      console.log('🔍 多字段搜索结果:', data.multiFieldSearchEquipments?.equipments?.length || 0, '条数据');
      console.log('🔍 分页信息:', {
        offset: data.multiFieldSearchEquipments?.offset,
        limit: data.multiFieldSearchEquipments?.limit,
        totalCount: data.multiFieldSearchEquipments?.totalCount
      });
      
      return data.multiFieldSearchEquipments?.equipments || [];
    }

}

export default new GraphQLService();